import type {
  LastGame,
  LastGamesGetResponse,
  LastGamesPostPayload,
  LastGamesStorage,
} from '~/types/last-game';
import { FetchLastGamesHandler } from '~/handlers/fetch-last-games.handler';
import { UpdateLastGamesHandler } from '~/handlers/update-last-games.handler';
import { ApostatudoApiFetchLastGamesAdapter } from '~/adapters/api-fetch-last-games/apostatudo-api-fetch-last-games.adapter';
import { ApostatudoApiUpdateLastGamesAdapter } from '~/adapters/api-update-last-games/apostatudo-api-update-last-games.adapter';

const STORAGE_KEY = 'ultimos_jogos';

// Sistema de eventos para notificar componentes sobre mudanças
const lastGamesUpdateCallbacks = new Set<() => void>();

const notifyLastGamesUpdate = () => {
  lastGamesUpdateCallbacks.forEach((callback) => callback());
};

export const useLastGames = () => {
  const fetchHandler = new FetchLastGamesHandler(
    new ApostatudoApiFetchLastGamesAdapter(),
  );

  const updateHandler = new UpdateLastGamesHandler(
    new ApostatudoApiUpdateLastGamesAdapter(),
  );

  const getFromStorage = (): LastGamesStorage | null => {
    if (typeof window === 'undefined') return null;

    try {
      const stored = localStorage.getItem(STORAGE_KEY);

      if (!stored) return null;

      return JSON.parse(stored);
    } catch {
      return null;
    }
  };

  const saveToStorage = (userId: number, games: LastGame[]): void => {
    if (typeof window === 'undefined') return;

    try {
      // Normaliza slugs para lowercase
      const normalizedGames = games.map((game) => ({
        ...game,
        slug: game.slug.toLowerCase(),
      }));

      const storageData: LastGamesStorage = {
        user_id: userId,
        payload: normalizedGames,
        updatedAt: new Date().toISOString(),
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));

      // Notifica componentes sobre a atualização
      notifyLastGamesUpdate();
    } catch {}
  };

  const clearStorage = (): void => {
    if (typeof window === 'undefined') return;

    try {
      localStorage.removeItem(STORAGE_KEY);

      // Notifica componentes sobre a limpeza
      notifyLastGamesUpdate();
    } catch {}
  };

  const fetchAndStore = async (
    userId: number,
  ): Promise<LastGamesGetResponse | null> => {
    try {
      const data = await fetchHandler.handle(userId);

      if (data && data.length > 0) {
        saveToStorage(userId, data);

        return data;
      } else {
        clearStorage();

        return null;
      }
    } catch {
      return null;
    }
  };

  const addGame = async (userId: number, gameId: number): Promise<boolean> => {
    try {
      const currentData = getFromStorage();
      const games: LastGame[] = currentData?.payload || [];

      // Verifica se o jogo já está no topo da lista
      if (games.length > 0 && games[0]?.game_id === gameId) {
        return true;
      }

      // POST apenas o game_id
      const payload: LastGamesPostPayload = {
        user_id: userId,
        game_id: gameId,
      };

      const result = await updateHandler.handle(payload);

      if (result && result.last_games && result.last_games.length > 0) {
        // Backend já retorna a lista atualizada no campo last_games
        saveToStorage(userId, result.last_games);

        return true;
      }

      return false;
    } catch {
      return false;
    }
  };

  const getLastGames = (): LastGame[] => {
    const data = getFromStorage();

    return data?.payload || [];
  };

  const getLastPlayedGame = (): LastGame | null => {
    const games = getLastGames();

    return games.length > 0 ? (games[0] ?? null) : null;
  };

  const hasGames = (): boolean => {
    const games = getLastGames();

    return games.length > 0;
  };

  const onLastGamesUpdate = (callback: () => void): (() => void) => {
    lastGamesUpdateCallbacks.add(callback);

    // Retorna função para remover o listener
    return () => {
      lastGamesUpdateCallbacks.delete(callback);
    };
  };

  return {
    fetchAndStore,
    addGame,
    getLastGames,
    getLastPlayedGame,
    hasGames,
    clearStorage,
    getFromStorage,
    onLastGamesUpdate,
  };
};
